<script setup lang="ts">
/**
 * 图标组件
 *
 * 规格（Codex 交接 §4）：24x24 viewBox、1.5px 描边、线性风格。
 * 形态参考 Lucide；⛔ 禁止 Emoji 和 Unicode 符号充当图标。
 */
defineProps<{
  name: string;
  size?: number;
}>();

/** 路径表 —— 全部来自 Lucide 风格的 24x24 线性图标 */
const PATHS: Record<string, string> = {
  // 导航
  sparkle: 'M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.8 2.8M14.9 14.9l2.8 2.8M17.7 6.3l-2.8 2.8M9.1 14.9l-2.8 2.8',
  target: 'M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16zM12 9.4a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2zM12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22',
  fileText: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5zM14 3v5h5M9 13h6M9 17h4',
  clock: 'M12 3.2a8.8 8.8 0 1 0 0 17.6 8.8 8.8 0 0 0 0-17.6zM12 7v5.2l3.2 1.9',
  grid: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z',
  /** 数据复盘（用柱状图表示"效果怎么样"）。没有它会掉到默认图标，导航里认不出来 */
  chart: 'M4 20h16M6.5 20v-6M11 20V8M15.5 20v-9M20 20V5',
  layers: 'M12 3l8.5 4.5L12 12 3.5 7.5 12 3zM4 12.5l8 4.2 8-4.2M4 16.8l8 4.2 8-4.2',
  bot: 'M5 8h14a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1zM12 4.5V8M9 13h.01M15 13h.01M9.5 16h5M12 3a1.2 1.2 0 1 0 0 2.4A1.2 1.2 0 0 0 12 3z',
  folder: 'M4 7a2 2 0 0 1 2-2h3.5l2 2H18a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7z',
  settings:
    'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a1.9 1.9 0 1 1-2.7 2.7l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5v.2a1.9 1.9 0 1 1-3.8 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a1.9 1.9 0 1 1-2.7-2.7l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a1.9 1.9 0 1 1 0-3.8h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a1.9 1.9 0 1 1 2.7-2.7l.1.1a1.6 1.6 0 0 0 1.8.3h.1a1.6 1.6 0 0 0 1-1.5V3a1.9 1.9 0 1 1 3.8 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a1.9 1.9 0 1 1 2.7 2.7l-.1.1a1.6 1.6 0 0 0-.3 1.8v.1a1.6 1.6 0 0 0 1.5 1h.2a1.9 1.9 0 1 1 0 3.8h-.1a1.6 1.6 0 0 0-1.5 1z',
  help: 'M12 3.2a8.8 8.8 0 1 0 0 17.6 8.8 8.8 0 0 0 0-17.6zM9.6 9.4a2.5 2.5 0 1 1 3.4 2.33c-.6.24-1 .85-1 1.5v.37M12 17h.01',
  users:
    'M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20M10 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM20 20v-1.5a3.5 3.5 0 0 0-2.6-3.4M15.5 4.7a3.5 3.5 0 0 1 0 6.8',

  // 操作
  plus: 'M12 5.5v13M5.5 12h13',
  arrowUp: 'M12 19V5.5M6 11.5L12 5.5l6 6',
  arrowRight: 'M5 12h13M13 6.5l5.5 5.5-5.5 5.5',
  chevronDown: 'M7 10l5 5 5-5',
  chevronRight: 'M10 7l5 5-5 5',
  search: 'M11 4.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zM20 20l-4.4-4.4',
  wand: 'M6 18L18 6M15 6h3v3M9.5 4v3M8 5.5h3M4.5 11v2.5M3.2 12.2h2.6',
  pen: 'M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17v3zM14.5 6.5l3 3',
  video: 'M3.5 6.5A1.5 1.5 0 0 1 5 5h8a1.5 1.5 0 0 1 1.5 1.5v11A1.5 1.5 0 0 1 13 19H5a1.5 1.5 0 0 1-1.5-1.5v-11zM14.5 9.5l6-3.5v12l-6-3.5',
  clipboard: 'M9 3.5h6a1 1 0 0 1 1 1V6H8V4.5a1 1 0 0 1 1-1zM8 6H6.5A1.5 1.5 0 0 0 5 7.5v12A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5v-12A1.5 1.5 0 0 0 17.5 6H16',
  check: 'M5 12.5l4.5 4.5L19 7',
  /** 主推套餐用（后台充值套餐页）。没有它的话图标位置会空着，看不出哪个是主推 */
  star: 'M12 4l2.4 4.9 5.4.8-3.9 3.8.9 5.4L12 16.4 7.2 18.9l.9-5.4L4.2 9.7l5.4-.8L12 4z',
  x: 'M6 6l12 12M18 6L6 18',
  upload: 'M12 16V4.5M7 9.5L12 4.5l5 5M4 16v2.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V16',
  image: 'M4 5.5h16a.5.5 0 0 1 .5.5v12a.5.5 0 0 1-.5.5H4a.5.5 0 0 1-.5-.5V6a.5.5 0 0 1 .5-.5zM8.6 10.6a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2zM4 16l4.8-4.4a1.7 1.7 0 0 1 2.3 0l5.1 4.7',
  play: 'M6 4.5l13 7.5-13 7.5z',
  shield: 'M12 3l7.5 3v6c0 4.4-3.1 8.2-7.5 9.5C7.6 20.2 4.5 16.4 4.5 12V6L12 3zM9 12.2l2.2 2.2L15.5 10',
  warning: 'M10.3 3.9L2.5 17.4A2 2 0 0 0 4.2 20.4h15.6a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9.5v4M12 17h.01',
  info: 'M12 3.2a8.8 8.8 0 1 0 0 17.6 8.8 8.8 0 0 0 0-17.6zM12 11v5M12 7.8h.01',
  bell: 'M18 9a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6zM13.7 19a2 2 0 0 1-3.4 0',
  coins: 'M12 3.5c3.9 0 7 1.34 7 3s-3.1 3-7 3-7-1.34-7-3 3.1-3 7-3zM5 6.5v5c0 1.66 3.13 3 7 3s7-1.34 7-3v-5M5 11.5v5c0 1.66 3.13 3 7 3s7-1.34 7-3v-5',
  link: 'M10 13.5a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1.4 1.4M14 10.5a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1.4-1.4',
  list: 'M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01',
  slash: 'M17 4L7 20',
  refresh: 'M20 11a8 8 0 1 0-1.6 5.4M20 5v6h-6',
  download: 'M12 4v11.5M7 11l5 5 5-5M4 18.5V20a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-1.5',
  share: 'M12 15V4M8 7.5L12 3.5l4 4M5 14v5.5A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5V14',
  panel: 'M4 5.5h16a.5.5 0 0 1 .5.5v12a.5.5 0 0 1-.5.5H4a.5.5 0 0 1-.5-.5V6a.5.5 0 0 1 .5-.5zM9.5 5.5v13',
  menu: 'M4 7h16M4 12h16M4 17h16',
};

/** 需要填充而非描边的图标 */
const FILLED = new Set(['play']);
</script>

<template>
  <svg
    class="icon"
    :width="size ?? 20"
    :height="size ?? 20"
    viewBox="0 0 24 24"
    :style="FILLED.has(name) ? { fill: 'currentColor', stroke: 'none' } : undefined"
    aria-hidden="true"
    focusable="false"
  >
    <path :d="PATHS[name] ?? PATHS.info" />
  </svg>
</template>
